<template>
  <v-card>
    <!-- Header Section -->
    <v-card-title class="d-flex justify-space-between align-center">
      <div>
        <div class="headline">Review Requests in Period</div>
        <div class="d-flex flex-wrap mt-1" style="gap: 16px;">
          <v-chip small outlined>
            <v-icon small left>mdi-file-document-outline</v-icon>
            {{ totalFilteredResults }} Cases
          </v-chip>
          <v-chip small outlined color="success">
            <v-icon small left>mdi-star</v-icon>
            {{ casesWithReview }} With Review ({{ casesWithReviewPercentage }}%)
          </v-chip>
          <v-chip small outlined color="warning">
            <v-icon small left>mdi-star-outline</v-icon>
            {{ casesWithoutReview }} Without Review
          </v-chip>
        </div>
      </div>
    </v-card-title>

    <v-divider></v-divider>

    <!-- Table Controls -->
    <v-card-text class="py-2">
      <v-row align="center" dense>
        <!-- Filters -->
        <v-col cols="auto" style="min-width: 150px;">
          <v-select
            v-model="reviewFilter"
            :items="reviewFilterOptions"
            label="Review Filter"
            hide-details
            dense
            outlined
          ></v-select>
        </v-col>

        <v-spacer></v-spacer>

        <!-- Search and Actions -->
        <v-col cols="auto" style="min-width: 200px;">
          <v-text-field
            @keyup="updateSearch"
            append-icon="mdi-magnify"
            label="Search"
            hide-details
            dense
            outlined
            single-line
          ></v-text-field>
        </v-col>
        <v-col cols="auto">
          <vue-json-to-csv
            :json-data="requestsFiltered"
            csv-title="Review_Requests_Report"
          >
            <v-btn small outlined>
              <v-icon small left>mdi-download</v-icon>
              Export
            </v-btn>
          </vue-json-to-csv>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Data Table -->
    <v-data-table
      :footer-props="{
        'items-per-page-options': [-1]
      }"
      :headers="headers"
      hide-default-footer
      :items="requestsFiltered"
      :item-key="item => item.caseId + '-' + item.noteId"
      :loading="$apollo.loading"
      :search="search"
      :sort-by="['noteDate']"
      :sort-desc="[true]"
      :item-class="getRowClass"
      @current-items="getFiltered"
    >
      <template v-slot:item.caseId="props">
        <a
          class="go-to-case-link mr-2"
          target="_blank"
          :href="
            `https://cs-web.airdesksolutions.com/SA/CaseSummary.aspx?CaseID=${props.item.caseId}`
          "
        >
          <v-icon size="15">mdi-open-in-new</v-icon>
        </a>
      </template>
      <template v-slot:item.noteDate="props">
        {{ formatDate(props.item.noteDate) }}
      </template>
      <template v-slot:item.noteText="props">
        <div
          class="note-text-cell"
          @click="openNoteDialog(props.item)"
          style="cursor: pointer;"
        >
          {{ stripHtml(props.item.noteText, 80) }}
          <v-icon x-small class="ml-1" color="grey">mdi-open-in-new</v-icon>
        </div>
      </template>
      <template v-slot:item.reviewRating="props">
        <v-chip
          v-if="props.item.reviewRating"
          :color="getReviewColor(props.item.reviewRating)"
          small
          dark
        >
          {{ formatReviewRating(props.item.reviewRating) }}
        </v-chip>
        <span v-else class="grey--text">-</span>
      </template>
    </v-data-table>

    <!-- Note Dialog -->
    <v-dialog v-model="noteDialog" max-width="600">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>Review Request Note</span>
          <v-btn icon @click="noteDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4" v-if="selectedNote">
          <div class="mb-3">
            <strong>Client:</strong> {{ selectedNote.clientName }}<br />
            <strong>Case #:</strong> {{ selectedNote.caseNumber }}<br />
            <strong>Requested By:</strong> {{ selectedNote.creatorName }}<br />
            <strong>Date:</strong> {{ formatDate(selectedNote.noteDate) }}
          </div>
          <v-divider class="mb-3"></v-divider>
          <div
            class="note-content"
            v-html="sanitize(selectedNote.noteText)"
          ></div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import filter from "lodash/filter";
import debounce from "lodash/debounce";
import VueJsonToCsv from "vue-json-to-csv";
import sanitizeHtml from "sanitize-html";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";

export default {
  data() {
    return {
      search: "",
      headers: [
        { text: "Action", value: "caseId", width: "80px" },
        { text: "Case #", value: "caseNumber", width: "100px" },
        { text: "Client", value: "clientName" },
        { text: "Attorney", value: "attorney" },
        { text: "Paralegal", value: "paralegal" },
        { text: "Request Date", value: "noteDate", width: "120px" },
        { text: "Requested By", value: "creatorName", width: "120px" },
        { text: "Note", value: "noteText" },
        { text: "Review", value: "reviewRating", width: "100px" },
        { text: "Review Date", value: "reviewDate", width: "120px" }
      ],
      reviewRequests: [],
      reviewFilter: "all",
      reviewFilterOptions: [
        { text: "All Cases", value: "all" },
        { text: "Has Review", value: "hasReview" },
        { text: "No Review", value: "noReview" },
        { text: "5-Star", value: "5-star" },
        { text: "4-Star", value: "4-star" },
        { text: "3-Star", value: "3-star" },
        { text: "2-Star", value: "2-star" },
        { text: "1-Star", value: "1-star" }
      ],
      filteredResults: [],
      totalFilteredResults: 0,
      noteDialog: false,
      selectedNote: null
    };
  },
  components: {
    VueJsonToCsv
  },
  methods: {
    updateSearch: debounce(function(e) {
      this.search = e.target.value;
    }, 500),
    getFiltered(items) {
      if (this.totalFilteredResults !== items?.length) {
        this.totalFilteredResults = items?.length;
      }
      if (JSON.stringify(this.filteredResults) !== JSON.stringify(items)) {
        this.filteredResults = items ? [...items] : [];
      }
      return false;
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      try {
        const date = parseISO(dateString);
        return format(date, "MM/dd/yyyy");
      } catch {
        return dateString.split(" ")[0];
      }
    },
    stripHtml(html, maxLength) {
      if (!html) return "";
      const text = html
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },
    sanitize(html) {
      if (!html) return "";
      return sanitizeHtml(html, {
        allowedTags: [
          "p",
          "br",
          "strong",
          "em",
          "b",
          "i",
          "u",
          "ul",
          "ol",
          "li",
          "div",
          "span"
        ],
        allowedAttributes: {}
      }).replace(/<p><\/p>/gi, "");
    },
    openNoteDialog(item) {
      this.selectedNote = item;
      this.noteDialog = true;
    },
    getRowClass(item) {
      if (!item.reviewRating) {
        return "no-review";
      }
      return "";
    },
    formatReviewRating(rating) {
      if (!rating) return "";
      const match = rating.match(/(\d)-star/i);
      return match ? `${match[1]}★` : rating;
    },
    getReviewColor(rating) {
      if (!rating) return "grey";
      const lowerRating = rating.toLowerCase();
      if (lowerRating.includes("5-star")) return "green";
      if (lowerRating.includes("4-star")) return "light-green";
      if (lowerRating.includes("3-star")) return "orange";
      if (lowerRating.includes("2-star")) return "deep-orange";
      if (lowerRating.includes("1-star")) return "red";
      return "grey";
    }
  },
  computed: {
    ...mapGetters(["startDate", "endDate"]),
    casesWithReview() {
      return filter(this.requestsFiltered, c => !!c.reviewRating).length;
    },
    casesWithoutReview() {
      return filter(this.requestsFiltered, c => !c.reviewRating).length;
    },
    casesWithReviewPercentage() {
      if (this.totalFilteredResults === 0) return 0;
      return ((this.casesWithReview / this.totalFilteredResults) * 100).toFixed(
        1
      );
    },
    requestsFiltered() {
      let filtered = this.reviewRequests;

      if (this.reviewFilter !== "all") {
        if (this.reviewFilter === "hasReview") {
          filtered = filter(filtered, function(c) {
            return !!c.reviewRating;
          });
        } else if (this.reviewFilter === "noReview") {
          filtered = filter(filtered, function(c) {
            return !c.reviewRating;
          });
        } else {
          const starFilter = this.reviewFilter;
          filtered = filter(filtered, function(c) {
            return (
              c.reviewRating &&
              c.reviewRating.toLowerCase().includes(starFilter)
            );
          });
        }
      }

      return filtered;
    }
  },
  apollo: {
    reviewRequests: {
      query: gql`
        query($startDate: String!, $endDate: String!) {
          reviewRequests: reviewRequestsInPeriod(
            startDate: $startDate
            endDate: $endDate
          ) {
            caseId
            caseNumber
            clientName
            attorney
            paralegal
            noteId
            noteText
            noteDate
            creatorName
            reviewRating
            reviewDate
          }
        }
      `,
      variables() {
        return {
          startDate: this.startDate,
          endDate: this.endDate
        };
      }
    }
  }
};
</script>

<style scoped>
.note-text-cell {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1976d2;
}

.note-text-cell:hover {
  text-decoration: underline;
}

.note-content {
  line-height: 1.6;
  max-height: 400px;
  overflow-y: auto;
}

.note-content p {
  margin-bottom: 0.5em;
}

tr.no-review {
  background-color: #fff8e1 !important;
}

tr.no-review:hover {
  background-color: #ffecb3 !important;
}

tr.no-review td {
  background-color: inherit !important;
}
</style>
